import '../machine.css';

import { IonLabel, IonList } from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { merge } from 'lodash';
import { ReactElement, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Can } from '../../../api/authorization.provider';
import {
  ListMachinesDocument,
  RegisterMachineInput,
  useListScratchpadAppsLazyQuery,
  useRegisterMachineMutation,
} from '../../../api/graphql/generated';
import { useHasFeature } from '../../../api/hooks/feature.hooks';
import {
  trackReactivatingMachine,
  trackRegisterMachine,
} from '../../../config/analytics';
import { useRedirect } from '../../../pages/redirect.hooks';
import { useBeaconRouter } from '../../../route/route.hooks';
import { logger } from '../../../util/logger';
import { RightAlignedButtons } from '../../button/aligned-buttons';
import { FormSubmitButton } from '../../form/form-submit.button';
import { IonFormSwitchField } from '../../form/ion-form-switch.field';
import { IonFormField } from '../../form/ion-form.field';
import { SaveStatusToast } from '../../toast/toast.gql';
import { RegisterMachineProps } from './register-machine';

const schema = yup.object().shape({
  registrationId: yup.string().when('machine.demo', ([isDemo], _schema) => {
    if (isDemo) {
      return yup.string();
    } else {
      return yup.string().required('*').min(6, '*');
    }
  }),
  machine: yup.object().shape({
    active: yup.boolean().required(),
    demo: yup.boolean().required(),
    name: yup.string().required('*'),
  }),
});

const RegisterMachineCloud = (props: RegisterMachineProps): ReactElement => {
  const { teamId, machine, onComplete } = props;
  const canRegisterDemos = useHasFeature<boolean>(
    'beacon-demo-machine-registration'
  );
  const { i18n } = useTranslation();

  const redirect = useRedirect();

  const formikRef = useRef<FormikProps<RegisterMachineInput> | null>(null);

  const [registerMachineMutation, { loading, error }] =
    useRegisterMachineMutation();

  const [fetchScratchpadApps] = useListScratchpadAppsLazyQuery();

  const { builder } = useBeaconRouter();

  const onSubmit = async (form: RegisterMachineInput) => {
    let input = form;
    if (canRegisterDemos && input.machine.demo === true) {
      input = merge({}, input, {
        machine: {
          demo: true,
        },
      });
    }
    try {
      const response = await registerMachineMutation({
        variables: { input },
        refetchQueries: [ListMachinesDocument],
      });
      const machine = response.data?.registerMachine;
      if (machine) {
        logger.info('Successfully registered machine!');
        await fetchScratchpadApps({
          variables: { input: { machineId: machine.id } },
        });
        logger.info('Successfully fetched any scratchpad apps for machine');
        if (input.machine.id) {
          trackReactivatingMachine(machine);
        } else {
          trackRegisterMachine(machine);
        }

        if (onComplete) {
          onComplete(machine);
        } else {
          logger.info('Redirecting to machine page');
          redirect(builder.getMachineRoute(teamId, machine.id));
        }
      }
    } catch (error) {
      logger.error('Error registering machine', { error, input });
    }
  };

  const initialValues = useMemo(
    () => ({
      machine: {
        teamId,
        id: machine?.id,
        name: machine?.name,
        active: true,
        demo: false,
      },
    }),
    [machine, teamId]
  );

  return (
    <>
      <Formik<RegisterMachineInput>
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize={true}
        onSubmit={onSubmit}
        validateOnMount={true}
        innerRef={(instance) => (formikRef.current = instance)}
      >
        {({ isValid, values }) => {
          return (
            <Form>
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:machine_name')}
                  name="machine.name"
                  inputProps={{
                    type: 'text',
                    autocapitalize: 'on',
                  }}
                />
                {!values.machine.demo && (
                  <IonFormField
                    label={i18n.t('common:machine_registration_code')}
                    name="registrationId"
                    inputProps={{
                      type: 'text',
                      autocapitalize: 'on',
                    }}
                  />
                )}
                {canRegisterDemos && (
                  <IonFormSwitchField
                    label={`${i18n.t('common:machine_demo')}?`}
                    name="machine.demo"
                    inputProps={{
                      disabled: machine?.id ? true : false,
                    }}
                  />
                )}
              </IonList>
              <br />
              <Can I="create" a="machine">
                <RightAlignedButtons>
                  <FormSubmitButton
                    loading={loading}
                    disabled={!isValid}
                    label={
                      machine?.id
                        ? i18n.t('common:machine_reactivate_robot')
                        : i18n.t('common:machine_create')
                    }
                  />
                </RightAlignedButtons>
              </Can>
              {machine?.id === undefined && canRegisterDemos && (
                <div className="ion-padding ion-text-center">
                  <IonLabel color="medium">
                    {i18n.t('common:machine_demo_help')}
                  </IonLabel>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
      <SaveStatusToast loading={loading} error={error} />
    </>
  );
};

export { RegisterMachineCloud };
